/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description AddWriteOffReason Modal
 */
import _ from 'lodash';
import { Edit, Plus } from '@icon-park/react';
import { useEffect, useMemo, useState } from 'react';
import { Modal, Form, Input, Switch, Button, ButtonProps, Tooltip } from 'antd';

import useUpdateWriteOffReason from 'hooks/admin/mutate/useUpdateWriteOffReason';
import { useAppStoreValues } from 'store/app';
import { isDuplicateName } from 'utils/admin/entitySettings';
import { WRITE_OFF_REASON_CATEGORIES } from 'app-constants/admin/entitySettings';
import { NO_ACCESS_TOOLTIP_TEXT } from '@veritable/common/constants';

/**
 * initial state for add write-off reason
 */
const initialState: EntitySettings.IWriteOffReasonPost = {
	name: '',
	isDeleted: false,
	category: WRITE_OFF_REASON_CATEGORIES.user,
};

export interface IProps extends ButtonProps {
	reason?: EntitySettings.IWriteOffReasonResponse;
	iconOnly?: boolean;
	title?: string;
	existingNames?: string[];
}

/**
 * AddClaimAction
 * @returns {React.FC<IProps>} AddClaimAction
 */

const AddWriteOffReason: React.FC<IProps> = ({ reason, iconOnly, title, existingNames, ...restProps }) => {
	const [open, setOpen] = useState(false);
	const initialValues = useMemo(() => reason || initialState, [reason]);
	const [reasonMeta, setReasonMeta] = useState<EntitySettings.IWriteOffReasonPost | null>(initialValues);
	const { mutateAsync, isPending } = useUpdateWriteOffReason();
	const { canAddClaims } = useAppStoreValues().userAccess;
	const disableButton = restProps.disabled || !canAddClaims;
	const [form] = Form.useForm();

	const isDuplicate = isDuplicateName({
		initialName: reason?.name,
		currentName: reasonMeta?.name,
		allNames: existingNames,
	});

	const disabled =
		isDuplicate ||
		((_.isEmpty(reasonMeta?.name) || _.isEqual(reasonMeta?.name, reason?.name)) &&
			(_.isEmpty(reasonMeta?.name) || _.isEqual(reasonMeta?.isDeleted, !!reason?.isDeleted)));

	// use effect to set the activity meta
	useEffect(() => {
		if (!_.isEmpty(reason)) {
			setReasonMeta({ ...reason, category: WRITE_OFF_REASON_CATEGORIES.user });
		}
	}, [reason]);

	// handle cancel
	const handleCancel = () => {
		setOpen(false);
		setReasonMeta(_.cloneDeep(initialState));
		form?.resetFields();
	};

	// handle ok
	const handleOk = () => {
		form.validateFields().then(() => {
			const submitModel: ClaimRequest.IActivityTypePost = {
				...reasonMeta,
				id: reason?.id,
				description: reason?.description || reasonMeta?.name,
			};
			mutateAsync({ model: { writeOffReasonPost: submitModel } }, { onSuccess: handleCancel });
		});
	};

	// get button
	const getButton = () => {
		if (iconOnly) {
			return <Button icon={<Edit />} onClick={() => setOpen(true)} {...restProps} disabled={disableButton} />;
		}
		return (
			<Button onClick={() => setOpen(true)} type='link' icon={<Plus />} {...restProps} disabled={disableButton}>
				{'Add'}
			</Button>
		);
	};

	return (
		<>
			<Tooltip title={canAddClaims ? (iconOnly ? 'Edit' : '') : NO_ACCESS_TOOLTIP_TEXT}>{getButton()}</Tooltip>
			<Modal
				title={title || 'Add Write-Off Reason'}
				open={open}
				onCancel={handleCancel}
				onOk={handleOk}
				okText='Save'
				cancelText='Cancel'
				cancelButtonProps={{ disabled: isPending }}
				destroyOnHidden
				okButtonProps={{ disabled, loading: isPending }}
			>
				<Form colon={false} form={form} layout='vertical' initialValues={initialValues}>
					<Form.Item
						label='Reason'
						name='name'
						rules={[
							{ required: true, message: 'Please enter a reason' },
							{
								validator: (__, value) => {
									const duplicateName = isDuplicateName({
										initialName: reason?.name,
										currentName: value,
										allNames: existingNames,
									});
									if (duplicateName) {
										return Promise.reject('Write-Off reason already exists');
									}
									return Promise.resolve();
								},
							},
						]}
					>
						<Input
							value={reasonMeta?.name}
							placeholder='Enter reason'
							onChange={(e) => setReasonMeta({ ...reasonMeta, name: e.target.value })}
						/>
					</Form.Item>

					<Form.Item label='Status' name='enabled' style={{ marginBottom: 0 }}>
						<Switch checked={!reasonMeta?.isDeleted} onChange={(val) => setReasonMeta({ ...reasonMeta, isDeleted: !val })} />
					</Form.Item>
				</Form>
			</Modal>
		</>
	);
};

export default AddWriteOffReason;
